import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../globalInspector/axiosInstance";
import { env } from "../../../env";
import { toast } from "react-toastify";
import CommonToast from "../../../components/_utils/CommonToast";
import { GlobalConfiguration } from "../../../utils/configurationProvider";
import ICONS from "../../../assets/Icons";
const baseUrl = env.REACT_APP_BASE_URL;

const getErrorMessage = (error) => {
  const errors = error?.response?.data?.errors;
  if (typeof errors === "string") return errors;
  if (errors && typeof errors === "object") {
    if (
      Array.isArray(errors?.base) &&
      errors.base[GlobalConfiguration.lengthCheckZero]
    )
      return errors.base[GlobalConfiguration.lengthCheckZero];
    if (
      Array.isArray(errors?.user_unauthorized) &&
      errors.user_unauthorized[GlobalConfiguration.lengthCheckZero]
    )
      return errors.user_unauthorized[GlobalConfiguration.lengthCheckZero];
    if (
      Array.isArray(errors?.token_expired) &&
      errors.token_expired[GlobalConfiguration.lengthCheckZero]
    )
      return errors.token_expired[GlobalConfiguration.lengthCheckZero];
    return JSON.stringify(errors);
  }
  const baseMsg =
    error?.response?.data?.errors?.base?.[GlobalConfiguration.lengthCheckZero];
  return (
    baseMsg ||
    error?.response?.data?.message ||
    error?.message ||
    "Something went wrong"
  );
};

export const initializeTotp = createAsyncThunk(
  "initializeTotp",
  async (values, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/users/init_two_fa`,
        {
          id: values?.id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );
      if (response.data?.status && values?.type !== "Render") {
        setTimeout(() => {
          toast.dismiss();
        }, 100);
        values?.navigate(values?.route);
      }
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.dismiss();
      setTimeout(() => {
        if (errorMessage !== "Two Factor Authentication is already enabled")
          CommonToast({
            text: errorMessage,
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
      }, 300);

      if (
        error?.response?.data?.status === false &&
        error?.response?.data?.errors ===
          "Two Factor Authentication is already enabled" &&
        values?.type !== "Render"
      ) {
        values?.navigate(values?.route);
      }
      return rejectWithValue(errorMessage);
    }
  },
);

export const initializeTotpDup = createAsyncThunk(
  "initializeTotpDup",
  async (values, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/users/init_two_fa`,
        {
          id: values?.id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.dismiss();
      setTimeout(() => {
        if (errorMessage !== "Two Factor Authentication is already enabled")
          CommonToast({
            text: errorMessage,
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
      }, 300);

      return rejectWithValue(errorMessage);
    }
  },
);

export const disableTotp = createAsyncThunk(
  "disableTotp",
  async (values, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/users/disable_two_fa`,
        {
          id: values?.id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );
      values?.setCurrentDisplay("enabled");
      values?.setOpenDisabledPop(false);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      values?.setOpenDisabledPop(false);
      return rejectWithValue(errorMessage);
    }
  },
);

export const enableTotp = createAsyncThunk(
  "enableTotp",
  async (values, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/users/enable_two_fa`,
        {
          id: values?.id,
          totp: values?.totp,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      return rejectWithValue(errorMessage);
    }
  },
);

const totp = createSlice({
  name: "totp",
  initialState: {
    initializeTotpLoading: null,
    initializeTotpData: null,
    initializeTotpError: null,
    initializeTotpErrorMsg: null,
    initializeTotpLoadingDup: null,
    initializeTotpDataDup: null,
    initializeTotpErrorDup: null,
    initializeTotpErrorMsgDup: null,
    enableTotpLoading: null,
    enableTotpData: null,
    enableTotpError: null,
    disableTotpLoading: null,
    disableTotpData: null,
    disableTotpError: null,
  },
  reducers: {
    disabledEnabledError(state) {
      state.enableTotpError = null;
      state.enableTotpData = null;
    },
    removeInitialError(state) {
      state.initializeTotpLoading = null;
      state.initializeTotpData = null;
      state.initializeTotpError = null;
      state.initializeTotpErrorMsg = null;
    },
    resetDisabled(state) {
      state.disableTotpLoading = null;
      state.disableTotpData = null;
      state.disableTotpError = null;
    },
    resetDup(state) {
      state.initializeTotpErrorDup = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initializeTotp.pending, (state) => {
      state.initializeTotpLoading = true;
    });
    builder.addCase(initializeTotp.fulfilled, (state, action) => {
      state.initializeTotpLoading = false;
      state.initializeTotpData = action.payload;
    });
    builder.addCase(initializeTotp.rejected, (state, action) => {
      state.initializeTotpError = true;
      state.initializeTotpLoading = false;
      state.initializeTotpData = "Error";
      state.initializeTotpErrorMsg = action.payload;
    });

    builder.addCase(initializeTotpDup.pending, (state) => {
      state.initializeTotpLoadingDup = true;
    });
    builder.addCase(initializeTotpDup.fulfilled, (state, action) => {
      state.initializeTotpLoadingDup = false;
      state.initializeTotpDataDup = action.payload;
    });
    builder.addCase(initializeTotpDup.rejected, (state, action) => {
      state.initializeTotpErrorDup = true;
      state.initializeTotpLoadingDup = false;
      state.initializeTotpDataDup = "Error";
      state.initializeTotpErrorMsgDup = action.payload;
    });

    builder.addCase(enableTotp.pending, (state) => {
      state.enableTotpLoading = true;
      toast.dismiss();
      CommonToast({
        text: "Verifying authentication code",
        icon: "https://q2pay.in/new_assets/gifs/inputloader_white.gif",
        variant: "var(--processing)",
        buttonNeeded: false,
      });
    });
    builder.addCase(enableTotp.fulfilled, (state, action) => {
      state.enableTotpLoading = false;
      state.enableTotpData = action.payload;
      toast.dismiss();
      CommonToast({
        text: "TOTP has been enabled successfully.",
        icon: "https://q2pay.in/new_assets/common/approval/select_all_with_white_filled_circle.svg",
        variant: "var(--success)",
        buttonNeeded: false,
      });
    });
    builder.addCase(enableTotp.rejected, (state, action) => {
      state.enableTotpError = true;
      state.enableTotpLoading = false;
      toast.dismiss();
      CommonToast({
        text: action?.payload,
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
    });

    builder.addCase(disableTotp.pending, (state) => {
      state.disableTotpLoading = true;
      toast.dismiss();
      CommonToast({
        text: "Disabling TOTP",
        icon: "https://q2pay.in/new_assets/gifs/inputloader_white.gif",
        variant: "var(--processing)",
        buttonNeeded: false,
      });
    });
    builder.addCase(disableTotp.fulfilled, (state, action) => {
      state.disableTotpLoading = false;
      state.disableTotpData = action.payload;
      toast.dismiss();
      CommonToast({
        text: "TOTP disabled successfully.",
        icon: "https://q2pay.in/new_assets/common/approval/select_all_with_white_filled_circle.svg",
        variant: "var(--success)",
        buttonNeeded: false,
      });
      state.initializeTotpErrorMsgDup = "";
    });
    builder.addCase(disableTotp.rejected, (state, action) => {
      state.disableTotpError = true;
      state.disableTotpLoading = false;
      toast.dismiss();
      CommonToast({
        text: action?.payload,
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
    });
  },
});

export const {
  disabledEnabledError,
  removeInitialError,
  resetDisabled,
  resetDup,
} = totp.actions;

export default totp.reducer;
